import * as React from "react";
import {
  Box,
  InputAdornment,
  InputBaseComponentProps,
  TextField,
} from "@mui/material";

type InputPropsType = {
  label?: string;
  error?: boolean;
  helperText?: string;
  type?: string;
  onChangeCb?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  disabled?: boolean;
  value?: string;
  name?: string;
  startIcon?: React.ReactNode;
  endIcon?: React.ReactNode;
  size?: "small" | "medium";
  placeholder?: string;
  placeholderColor?: string;
  style?: Record<string, string>;
  textFieldLabel?: any;
  required?: boolean;
  multiline?: boolean;
  rows?: number;
  isStarPresent?: boolean;
  isDecimal?: boolean; // prop to control decimal behavior
  maxWords?: number;
  maxLength?: number;
  onBlur?: (e: any) => void;
  inputComponent?: React.ElementType<InputBaseComponentProps>;
  onkeydown?: (e: React.KeyboardEvent<HTMLInputElement>) => void; // Update prop name to lowercase
};

export default function Input({
  name = "",
  label = "",
  textFieldLabel = "",
  type = "text",
  error = false,
  helperText = "",
  placeholder = "",
  placeholderColor = "",
  isStarPresent = false,
  isDecimal = false,
  onChangeCb = () => {
    /* no-op */
  },
  disabled = false,
  value = "",
  startIcon,
  endIcon,
  size = "medium",
  style = {},
  required = false,
  multiline = false,
  rows = 2,
  maxWords,
  maxLength,
  onBlur = (e) => {},
  inputComponent,
  onkeydown, // Use lowercase here
}: InputPropsType) {
  const [inputValue, setInputValue] = React.useState(value || "");
  const [wordCount, setWordCount] = React.useState(
    inputValue.trim() ? inputValue.trim().split(/\s+/).length : 0
  );
  React.useEffect(() => {
    setInputValue(value || ""); // Update inputValue when value prop changes
  }, [value]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputText = e.target.value;
    if (inputText.startsWith(" ")) return;

    const words = inputText.trim().split(/\s+/).filter(Boolean);

    if (!maxWords || words.length <= maxWords) {
      if (!isDecimal) {
        setInputValue(inputText); // Update inputValue only if isDecimal is false
      }
      setWordCount(words.length);
      if (onChangeCb) onChangeCb(e);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (type === "number") {
      // Allow only numeric digits (0-9) and essential keys like Backspace, Delete, Arrow keys, etc.
      const allowedKeys = [
        "Backspace",
        "Delete",
        "ArrowLeft",
        "ArrowRight",
        "Tab",
        "Enter",
      ];
      if (
        !allowedKeys.includes(e.key) && // Allow essential keys
        !/^\d$/.test(e.key) // Allow only digits
      ) {
        e.preventDefault(); // Prevent other keys
      }
    }
    if (onkeydown) {
      onkeydown(e); // Call the passed onkeydown function if available
    }
  };

  return (
    <div className="flex flex-col items-start w-full h-full">
      {label && (
        <label className="font-weight-500 font-size-16px color-212D30 mb-3">
          {label}
        </label>
      )}
      <TextField
        label={
          textFieldLabel ? (
            <>
              {textFieldLabel}{" "}
              {isStarPresent && <span style={{ color: "#CE0718" }}>*</span>}
            </>
          ) : undefined
        }
        className="w-full"
        name={name}
        type={type}
        rows={rows}
        variant="outlined"
        error={error}
        helperText={helperText}
        placeholder={placeholder}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        disabled={disabled}
        value={inputValue}
        multiline={multiline}
        size={size}
        onBlur={onBlur}
        onWheel={(e) => {
          // Prevent the mouse wheel from changing a focused number input's value.
          if (type === "number") {
            (e.target as HTMLElement).blur();
          }
        }}
        sx={{
          ...(placeholderColor && {
            "& .MuiInputBase-input::placeholder": {
              color: placeholderColor,
              opacity: 1,
            },
          }),
          "& input[type=number]": {
            MozAppearance: "textfield", // For Firefox
          },
          "& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button":
            {
              WebkitAppearance: "none", // For Chrome, Safari, Edge
              margin: 0,
            },
          "& .Mui-disabled": {
            backgroundColor: "#F4F4F4", // Set background for disabled state
          },
        }}
        slotProps={{
          input: {
            ...(startIcon
              ? {
                  startAdornment: (
                    <InputAdornment position="start">
                      {startIcon}
                    </InputAdornment>
                  ),
                }
              : {}),
            ...(endIcon
              ? {
                  endAdornment: (
                    <InputAdornment position="start">{endIcon}</InputAdornment>
                  ),
                }
              : maxWords
                ? {
                    endAdornment: (
                      <InputAdornment
                        position="end"
                        sx={{
                          position: "absolute",
                          bottom: multiline ? 0 : "auto",
                          right: 8,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "flex-end",
                        }}
                      >
                        <Box
                          sx={{
                            fontSize: "0.75rem",
                            color: wordCount > maxWords ? "#CE0718" : "#888",
                          }}
                        >
                          {wordCount}/{maxWords}
                        </Box>
                      </InputAdornment>
                    ),
                  }
                : null),
            inputComponent:
              inputComponent as React.ElementType<InputBaseComponentProps>,
          },
        }}
        inputProps={{
          maxLength,
        }}
        style={style}
        required={required}
      />
    </div>
  );
}
